// The pose of an element as the Properties panel edits it: the point its X/Y fields place, and the
// heading the elements drawn from a point onwards leave it in. Pure geometry, so it serves segments,
// walls and every node shape the same way.

/** Marker of the anchor point on the canvas: the one the panel's X/Y fields move. */
export const POSE_ANCHOR_COLOR = '#22c55e';

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;

/** The point X/Y refer to: the first node of a shape, the first end of an element drawn from a point. */
export function anchorOf(obj) {
  const first = obj.points?.[0];
  return first ? { x: first.x, y: first.y } : { x: obj.x1, y: obj.y1 };
}

/** Only an element drawn from a point onwards has a direction of its own: a node shape has none. */
export function hasHeading(obj) {
  return !obj.points && Number.isFinite(obj.x2) && Number.isFinite(obj.y2);
}

/** The direction the element leaves its anchor in, degrees: 0 = right, growing clockwise on screen. */
export function headingDeg(obj) {
  const to = obj.points ? obj.points[1] : { x: obj.x2, y: obj.y2 };
  if (!to) return 0;
  const from = anchorOf(obj);
  return Math.atan2(to.y - from.y, to.x - from.x) * RAD_TO_DEG;
}

/** A copy of `node` translated by (dx, dy), its Bezier handles included. */
export function translateNode(node, dx, dy) {
  const out = { x: node.x + dx, y: node.y + dy };
  if (node.hIn) out.hIn = { x: node.hIn.x + dx, y: node.hIn.y + dy };
  if (node.hOut) out.hOut = { x: node.hOut.x + dx, y: node.hOut.y + dy };
  return out;
}

/** Moves the element so its anchor lands on (x, y). Reports whether it moved at all. */
export function moveTo(obj, x, y) {
  const from = anchorOf(obj);
  const dx = x - from.x;
  const dy = y - from.y;
  if (!dx && !dy) return false;
  shift(obj, dx, dy);
  return true;
}

/** Turns the element around its anchor so it leaves the point in `deg`: the anchor itself stays put. */
export function turnTo(obj, deg) {
  const radians = (deg - headingDeg(obj)) * DEG_TO_RAD;
  if (!radians) return false;
  rotate(obj, anchorOf(obj), radians);
  return true;
}

/** Translates a whole element: either its nodes, or its ends, control point and handles. */
function shift(obj, dx, dy) {
  if (obj.points) {
    obj.points = obj.points.map((node) => translateNode(node, dx, dy));
    return;
  }
  obj.x1 += dx; obj.y1 += dy;
  obj.x2 += dx; obj.y2 += dy;
  if (obj.cx != null) { obj.cx += dx; obj.cy += dy; }
  if (obj.hIn) obj.hIn = { x: obj.hIn.x + dx, y: obj.hIn.y + dy };
  if (obj.hOut) obj.hOut = { x: obj.hOut.x + dx, y: obj.hOut.y + dy };
}

function rotatePoint(point, pivot, radians) {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos };
}

function rotateNode(node, pivot, radians) {
  const out = rotatePoint(node, pivot, radians);
  if (node.hIn) out.hIn = rotatePoint(node.hIn, pivot, radians);
  if (node.hOut) out.hOut = rotatePoint(node.hOut, pivot, radians);
  return out;
}

/** Turns a whole element around `pivot`, so the shape keeps its form. */
function rotate(obj, pivot, radians) {
  if (obj.points) {
    obj.points = obj.points.map((node) => rotateNode(node, pivot, radians));
    return;
  }
  const end = rotatePoint({ x: obj.x2, y: obj.y2 }, pivot, radians);
  obj.x2 = end.x; obj.y2 = end.y;
  if (obj.cx != null) {
    const control = rotatePoint({ x: obj.cx, y: obj.cy }, pivot, radians);
    obj.cx = control.x; obj.cy = control.y;
  }
  if (obj.hIn) obj.hIn = rotatePoint(obj.hIn, pivot, radians);
  if (obj.hOut) obj.hOut = rotatePoint(obj.hOut, pivot, radians);
}
